import { useCallback, useEffect, useState } from "react"

import { api, type AlcanceDeConfiguracoes, type CargoNoCadastro, type RespostaDosCargos } from "../api"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../componentes/Dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Skeleton } from "../componentes/Skeleton"
import { toast } from "../componentes/Toast"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { Escolha } from "../componentes/Escolha"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { inteiro } from "../formato"
import { BOTAO_BRANCO, BOTAO_CANCELAR, CABECALHO_DO_SHEET, MAIUSCULA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

type AbaDosCargos = "todos" | "ativos" | "inativos"

const ABAS_DOS_CARGOS: DescricaoDeAba<AbaDosCargos>[] = [
  { chave: "todos", rotulo: "Todos", dica: "Mostra todos os cargos" },
  { chave: "ativos", rotulo: "Ativos", dica: "Mostra só os cargos que aparecem na lista de escolha" },
  { chave: "inativos", rotulo: "Inativos", dica: "Mostra só os cargos desativados" },
]

const POR_PAGINA = 10

type Gaveta = { modo: "novo" } | { modo: "editar"; cargo: CargoNoCadastro } | null

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()

const paraMostrar = (nome: string) => nome.trim().replace(/\s+/g, " ").toLocaleUpperCase("pt-BR")

const contasQueContinuam = (quantas: number) =>
  quantas === 0
    ? "Nenhuma conta tem este cargo."
    : quantas === 1
      ? "A conta que o tem continua com ele."
      : `As ${inteiro(quantas)} contas que o têm continuam com ele.`

const contasQueMudam = (quantas: number, origem: string, destino: string) =>
  quantas === 0
    ? `Nenhuma conta tem ${origem}.`
    : quantas === 1
      ? `A conta com ${origem} passa a ${destino}.`
      : `As ${inteiro(quantas)} contas com ${origem} passam a ${destino}.`

export function Cargos({
  alcance,
  aoVerOndeEUsado,
}: {
  alcance: AlcanceDeConfiguracoes
  aoVerOndeEUsado?: (nome: string) => void
}) {
  const [cargos, definirCargos] = useState<CargoNoCadastro[] | null>(null)
  const [podeAlterar, definirPode] = useState(false)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDosCargos>("todos")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [gaveta, definirGaveta] = useState<Gaveta>(null)
  const [desativando, definirDesativando] = useState<CargoNoCadastro | null>(null)
  const [juntando, definirJuntando] = useState<CargoNoCadastro | null>(null)

  const aplicar = useCallback((resposta: RespostaDosCargos) => {
    definirCargos(resposta.dados)
    definirPode(resposta.pode_alterar)
    definirFalha(null)
  }, [])

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .cadastroDeCargos()
      .then(aplicar)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [aplicar])

  useEffect(carregar, [carregar])

  if (cargos === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cargos</CardTitle>
        </CardHeader>
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const procurado = normalizar(busca)
  const filtrados = cargos.filter(
    (c) => (aba === "todos" || (aba === "ativos") === c.ativo) && (procurado === "" || normalizar(c.nome).includes(procurado)),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtrados.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtrados.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const mudar = (cargo: CargoNoCadastro, ativo: boolean) => {
    api
      .alterarCargo(cargo.id, { ativo })
      .then((resposta) => {
        aplicar(resposta)
        toast.success(`Cargo ${cargo.nome} ${ativo ? "reativado" : "desativado"}`)
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const abrirGaveta = (nova: Gaveta) => {
    definirGaveta(nova)
  }

  const vazio =
    cargos.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Etiqueta />
          </EmptyMedia>
          <EmptyTitle>Nenhum cargo cadastrado</EmptyTitle>
          <EmptyDescription>{podeAlterar ? "Cadastre o primeiro cargo das contas." : "Só o Root cadastra."}</EmptyDescription>
        </EmptyHeader>
        {podeAlterar && (
          <EmptyContent>
            <Button type="button" className="h-10" onClick={() => definirGaveta({ modo: "novo" })}>
              <Icones.Mais />
              Adicionar cargo
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nada encontrado</EmptyTitle>
          <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nada nesta situação."}</EmptyDescription>
        </EmptyHeader>
        {busca !== "" && (
          <EmptyContent>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Cargos{" "}
          <ComDica
            dica={
          podeAlterar
            ? "Os cargos valem para todas as entidades. Se desativado, sai da lista de escolha e continua nas contas que já o têm."
            : alcance.conta_de_plataforma
              ? "Os cargos valem para todas as entidades, inclusive as reais, e esta é a conta de demonstração: ela os consulta, e só o Root os altera."
              : "Os cargos valem para todas as entidades, e só o Root os altera."
          }
          />
        </CardTitle>
      </CardHeader>


      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtrados.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          acaoNoTopo={
            podeAlterar && (
              <Button type="button" className="h-10" onClick={() => abrirGaveta({ modo: "novo" })}>
                <Icones.Mais />
                Adicionar cargo
              </Button>
            )
          }
          aoClicarNaLinha={podeAlterar ? (item) => definirGaveta({ modo: "editar", cargo: item }) : undefined}
          abasNaBarra={
            <Abas
              nome="cargos"
              rotulo="Situação dos cargos"
              classe="mb-0"
              abas={ABAS_DOS_CARGOS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome" />
          <Column<CargoNoCadastro> name="nome" label="Cargo" enableSorting={false} render={(valor) => <span className="font-medium">{String(valor)}</span>} />
          <Column<CargoNoCadastro> name="contas" label="Contas" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<CargoNoCadastro>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, cargo) => <Selo situacao={cargo.ativo ? "ativa" : "inativa"}>{cargo.ativo ? "Ativo" : "Inativo"}</Selo>}
          />
          {podeAlterar && <Acao<CargoNoCadastro> label="Editar" icon={Icones.Anotacao} onClick={(cargo) => abrirGaveta({ modo: "editar", cargo })} />}
          {podeAlterar && (
            <Acao<CargoNoCadastro>
              label="Juntar a outro cargo"
              icon={Icones.Juntada}
              onClick={(cargo) => {
                definirJuntando(cargo)
              }}
            />
          )}
          <Acao<CargoNoCadastro>
            label="Ver onde é usado"
            icon={Icones.Buscar}
            visivel={(item) => item.contas > 0}
            onClick={(item) => aoVerOndeEUsado?.(item.nome)}
          />
          {podeAlterar && <Acao<CargoNoCadastro> label="Desativar" icon={Icones.Dispensar} visivel={(cargo) => cargo.ativo} onClick={definirDesativando} />}
          {podeAlterar && <Acao<CargoNoCadastro> label="Reativar" icon={Icones.Reabrir} visivel={(cargo) => !cargo.ativo} onClick={(cargo) => mudar(cargo, true)} />}
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar o cargo {desativando?.nome}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Ele sai da lista de escolha. {contasQueContinuam(desativando?.contas ?? 0)} É possível reativar depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, false)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={juntando !== null} onOpenChange={(aberto) => !aberto && definirJuntando(null)}>
        <DialogContent>
          {juntando !== null && (
            <Juncao
              key={juntando.id}
              origem={juntando}
              cargos={cargos}
              aoCancelar={() => definirJuntando(null)}
              aoJuntar={(resposta, destino) => {
                aplicar(resposta)
                definirJuntando(null)
                toast.success(`${juntando.nome} juntado a ${destino}`)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && definirGaveta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {gaveta?.modo === "editar" ? `Cargo: ${gaveta.cargo.nome}` : "Novo cargo"}
            </SheetTitle>
          </SheetHeader>
          {gaveta !== null && (
            <FormularioDoCargo
              key={gaveta.modo === "editar" ? gaveta.cargo.id : "novo"}
              inicial={gaveta.modo === "editar" ? gaveta.cargo : null}
              cargos={cargos}
              aoCancelar={() => definirGaveta(null)}
              aoSalvar={(resposta, nome) => {
                aplicar(resposta)
                definirGaveta(null)
                toast.success(`Cargo ${paraMostrar(nome)} salvo`)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

function Juncao({
  origem,
  cargos,
  aoCancelar,
  aoJuntar,
}: {
  origem: CargoNoCadastro
  cargos: CargoNoCadastro[]
  aoCancelar: () => void
  aoJuntar: (resposta: RespostaDosCargos, destino: string) => void
}) {
  const [destinoId, definirDestinoId] = useState("")
  const [enviando, definirEnviando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)
  const opcoes = cargos.filter((c) => c.ativo && c.id !== origem.id)
  const destino = opcoes.find((c) => String(c.id) === destinoId)
  const falta = faltaNoFormulario([["o cargo que fica", destinoId]])

  const juntar = () => {
    if (destino === undefined) return
    definirEnviando(true)
    definirRecusa(null)
    api
      .juntarCargos(origem.id, destino.id)
      .then((resposta) => aoJuntar(resposta, destino.nome))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Juntar {origem.nome} a outro cargo</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div>
          <RotuloDoCampo htmlFor="cargo-que-fica" obrigatorio>
            Cargo que fica
          </RotuloDoCampo>
          <Escolha
            id="cargo-que-fica"
            obrigatorio
            valor={destinoId}
            aoMudar={definirDestinoId}
            vazio="Escolha o cargo"
            placeholderDaBusca="Buscar pelo nome do cargo"
            opcoes={opcoes.map((c) => ({ valor: String(c.id), rotulo: c.nome }))}
          />
        </div>
        <DialogDescription className="mt-3 opacity-100">
          {destino === undefined
            ? `As contas com ${origem.nome} passam ao cargo escolhido.`
            : contasQueMudam(origem.contas, origem.nome, destino.nome)}{" "}
          {origem.nome} fica desativado e pode ser reativado depois.
        </DialogDescription>
        {recusa !== null && (
          <p role="alert" className={cn(RECUSA, MAIUSCULA, "mt-2")}>
            {recusa}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="button" disabled={falta !== null || enviando} title={falta ?? undefined} onClick={juntar}>
          <Icones.Juntada />
          Juntar
        </Button>
      </DialogFooter>
    </>
  )
}

function FormularioDoCargo({
  inicial,
  cargos,
  aoCancelar,
  aoSalvar,
}: {
  inicial: CargoNoCadastro | null
  cargos: CargoNoCadastro[]
  aoCancelar: () => void
  aoSalvar: (resposta: RespostaDosCargos, nome: string) => void
}) {
  const [nome, definirNome] = useState(inicial?.nome ?? "")
  const [enviando, definirEnviando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const repetido = cargos.find((c) => c.id !== inicial?.id && normalizar(nome) !== "" && normalizar(c.nome) === normalizar(nome))
  const longo = nome.trim().length > 45
  const falta = faltaNoFormulario([["o nome", nome]])
  const bloqueio = falta ?? (repetido ? `Já existe o cargo ${repetido.nome}` : longo ? "O nome passa de 45 caracteres" : null)

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    const pedido = inicial === null ? api.criarCargo(nome) : api.alterarCargo(inicial.id, { nome })
    pedido
      .then((resposta) => aoSalvar(resposta, nome))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (bloqueio === null && !enviando) salvar()
      }}
    >
      <div>
        <RotuloDoCampo htmlFor="cargo-nome" obrigatorio>
          Nome
        </RotuloDoCampo>
        <Input
          id="cargo-nome"
          required
          autoComplete="off"
          maxLength={60}
          value={nome}
          aria-invalid={repetido !== undefined || longo || undefined}
          aria-describedby={repetido || longo ? "cargo-nome-aviso" : undefined}
          onChange={(evento) => definirNome(evento.target.value)}
        />
        {(repetido || longo) && (
          <p id="cargo-nome-aviso" className={cn(RECUSA, "mt-1.5")}>
            {repetido ? `Já existe o cargo ${repetido.nome}.` : "O nome passa de 45 caracteres."}
          </p>
        )}
      </div>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={bloqueio !== null || enviando} title={bloqueio ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}
